// useCountdown/index.ts
import { computed, onMounted, ref, unref, watch } from "vue";
import { MagicTimer } from "@maas/magic-timer";
import { DateTime } from "luxon";
var defaultOptions = {
  endDateTime: [1970, 1, 1, 0, 0, 0],
  zeroIndexedMonths: false
};
function useCountdown(options, callback) {
  options = { ...defaultOptions, ...options };
  if (unref(options.endDateTime).length < 3) {
    console.error(
      "useCountdownClock: \u201CendDateTime\u201D needs to be an array [YYYY, MM, DD, HH?, MM?, SS?]"
    );
  }
  const timezone = unref(options.timezone);
  if (timezone) {
    if (!DateTime.now().setZone(timezone).isValid) {
      console.error(
        `useCountdownClock: \u201C${timezone}\u201D is not a valid timezone. Did you mean ${DateTime.local().zoneName}? For more information see https://en.wikipedia.org/wiki/List_of_tz_database_time_zones`
      );
    }
  }
  const timer = new MagicTimer();
  const years = ref(0);
  const days = ref(0);
  const hours = ref(0);
  const minutes = ref(0);
  const seconds = ref(0);
  const parseDateTimeArray = (dateTimeArr) => {
    const [year, month, day, hour, minute, second] = dateTimeArr;
    return {
      year,
      month: options.zeroIndexedMonths ? month + 1 : month,
      day,
      hour: hour ?? 0,
      minute: minute ?? 0,
      second: second ?? 0
    };
  };
  const endDateTime = computed(() => {
    const { year, month, day, hour, minute, second } = parseDateTimeArray(
      unref(options.endDateTime)
    );
    return DateTime.fromObject(
      {
        year,
        month,
        day,
        hour,
        minute,
        second
      },
      {
        zone: unref(options.timezone)
      }
    );
  });
  function tick() {
    const now = DateTime.now().setZone(unref(options.timezone));
    const end = endDateTime.value;
    if (end <= now) {
      timer.stop();
      reset();
      if (callback) {
        callback();
      }
      return;
    }
    const diff = end.diff(now, [
      "years",
      "days",
      "hours",
      "minutes",
      "seconds",
      "milliseconds"
    ]).toObject();
    years.value = diff.years ?? 0;
    days.value = diff.days ?? 0;
    hours.value = diff.hours ?? 0;
    minutes.value = diff.minutes ?? 0;
    seconds.value = diff.seconds ?? 0;
  }
  function pad(value) {
    return ("0" + value).slice(-2);
  }
  function onTick(callback2) {
    timer.on("tick", callback2);
  }
  function reset() {
    years.value = 0;
    days.value = 0;
    hours.value = 0;
    minutes.value = 0;
    seconds.value = 0;
  }
  function restart() {
    timer.reset();
    timer.start();
  }
  timer.on("tick", tick);
  onMounted(() => {
    tick();
    timer.start();
  });
  watch(endDateTime, () => {
    if (timer.state === "running") {
      return;
    }
    restart();
  });
  return {
    years,
    days,
    hours,
    minutes,
    seconds,
    endDateTime,
    pad,
    restart,
    onTick
  };
}

// useEasings/index.ts
import {
  linear,
  easeInQuad,
  easeOutQuad,
  easeInOutQuad,
  easeOutBack,
  easeInCubic,
  easeOutCubic,
  easeInOutCubic,
  easeInQuart,
  easeOutQuart,
  easeInOutQuart,
  easeInQuint,
  easeOutQuint,
  easeInOutQuint
} from "@maas/vue-equipment/utils";
function useEasings() {
  const easings = {
    linear,
    easeInQuad,
    easeOutQuad,
    easeInOutQuad,
    easeOutBack,
    easeInCubic,
    easeOutCubic,
    easeInOutCubic,
    easeInQuart,
    easeOutQuart,
    easeInOutQuart,
    easeInQuint,
    easeOutQuint,
    easeInOutQuint
  };
  return easings;
}

// useMetaViewport/index.ts
import { ref as ref2 } from "vue";
function useMetaViewport(args) {
  const content = args?.content || "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0";
  const metaViewport = ref2(void 0);
  function setMetaViewport() {
    const metaElement = document.querySelector(
      'meta[name="viewport"]'
    );
    metaViewport.value = metaElement.content;
    metaElement.setAttribute("content", content);
  }
  function resetMetaViewport() {
    const metaElement = document.querySelector(
      'meta[name="viewport"]'
    );
    if (metaViewport.value) {
      metaElement.setAttribute("content", metaViewport.value);
    }
  }
  return {
    setMetaViewport,
    resetMetaViewport
  };
}

// useScrollTo/index.ts
import {
  unrefElement
} from "@vueuse/core";
import { easeOutQuad as easeOutQuad2 } from "@maas/vue-equipment/utils";
function min(a, b) {
  return a < b ? a : b;
}
function unwrapParent(parent) {
  if (parent === window) {
    return parent;
  } else if (typeof parent === "string") {
    return document.querySelector(parent) || document.documentElement;
  } else {
    return unrefElement(parent) || document.documentElement;
  }
}
function unwrapTarget(target, parentEl) {
  if (typeof target === "string") {
    const queryTarget = parentEl === window ? document : parentEl;
    return queryTarget.querySelector(target);
  } else {
    return unrefElement(target);
  }
}
function isHtmlElement(parentEl) {
  return parentEl instanceof HTMLElement;
}
function disableScrollSnap(parentEl) {
  if (isHtmlElement(parentEl)) {
    parentEl.style.scrollSnapType = "none";
  }
}
function reenableScrollSnap(parentEl) {
  if (isHtmlElement(parentEl)) {
    parentEl.style.scrollSnapType = "";
  }
}
function useScrollTo() {
  function getScrollPosition(element) {
    if (element === window) {
      return { x: window.scrollX, y: window.scrollY };
    } else if (element instanceof Element) {
      return { x: element.scrollLeft, y: element.scrollTop };
    } else {
      return { x: 0, y: 0 };
    }
  }
  function getDistance(target, parent) {
    const rect = target.getBoundingClientRect();
    const scrollEl = parent || document.scrollingElement || document.documentElement;
    const scrollTop = scrollEl instanceof Window ? 0 : scrollEl.scrollTop;
    const scrollLeft = scrollEl instanceof Window ? 0 : scrollEl.scrollLeft;
    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft
    };
  }
  function getScrollDuration({
    parent = document.documentElement || document.body,
    left = 0,
    top = 0,
    speed = 500
  }) {
    const currentPos = getScrollPosition(parent);
    const distanceX = currentPos.x - left;
    const distanceY = currentPos.y - top;
    const durationX = Math.abs(distanceX / speed * 100);
    const durationY = Math.abs(distanceY / speed * 100);
    return { x: durationX, y: durationY };
  }
  function scrollTo({
    parent = document.documentElement || document.body,
    top,
    left,
    duration = {},
    easing = easeOutQuad2,
    callback
  }) {
    const startTime = Date.now();
    const { x: fromX, y: fromY } = getScrollPosition(parent);
    if (fromX === top && fromY === left) {
      if (callback) callback();
      return;
    }
    const mappedDuration = { x: 500, y: 500, ...duration };
    const scroll = () => {
      const currentTime = Date.now();
      const timeX = min(1, (currentTime - startTime) / mappedDuration.x);
      const timeY = min(1, (currentTime - startTime) / mappedDuration.y);
      const easedTimeX = easing(timeX);
      const easedTimeY = easing(timeY);
      const minTime = Math.min(easedTimeX, easedTimeY);
      parent.scroll({
        left: minTime * (left - fromX) + fromX,
        top: minTime * (top - fromY) + fromY
      });
      if (easedTimeX < 1 || easedTimeY < 1) {
        requestAnimationFrame(scroll);
      } else if (callback) {
        callback();
      }
    };
    requestAnimationFrame(scroll);
  }
  function scrollToTarget({
    target,
    parent = document.documentElement || document.body,
    offset = {},
    speed = 500,
    easing = easeOutQuad2
  }) {
    const parentEl = unwrapParent(parent);
    if (!parentEl) return;
    const targetEl = unwrapTarget(target, parentEl);
    if (!targetEl) return;
    disableScrollSnap(parentEl);
    const mappedOffset = { x: 0, y: 0, ...offset };
    const distance = getDistance(targetEl, parentEl);
    const leftDistance = distance.left - mappedOffset.x;
    const topDistance = distance.top - mappedOffset.y;
    const scrollDuration = getScrollDuration({
      parent: parentEl,
      left: leftDistance,
      top: topDistance,
      speed
    });
    scrollTo({
      parent: parentEl,
      left: leftDistance,
      top: topDistance,
      duration: scrollDuration,
      easing,
      callback: () => reenableScrollSnap(parentEl)
    });
  }
  return {
    getScrollPosition,
    getDistance,
    getScrollDuration,
    scrollTo,
    scrollToTarget
  };
}
export {
  useCountdown,
  useEasings,
  useMetaViewport,
  useScrollTo
};
//# sourceMappingURL=index.js.map